import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import { EsignNoteInfoListItem, EsignNoteDetail, EsignLegalTextListItem, EsignData } from '../models/contract';
import { PdfDocumentViewerDialogData } from '../models/pdf-document-viewer';

@Injectable({
  providedIn: 'root'
})
export class ContractService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('ContractService');
  }

  public getEsignNoteInfoList(participantId: number): Observable<EsignNoteInfoListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Contract/PendingEsignNoteInfo';
    if (this._logging.serviceCallTraceLog) {
      console.log('ContractService.getEsignNoteInfoList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<EsignNoteInfoListItem>>(serviceUrl, { participantId: participantId }).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getEsignNoteInfoList', []))
    );
  }

  public getEsignNoteDetail(noteId: number): Observable<EsignNoteDetail> {
    const serviceUrl: string = this._baseServiceUrl + 'Contract/PendingEsignNoteDetail';
    if (this._logging.serviceCallTraceLog) {
      console.log('ContractService.getEsignNoteDetail :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<EsignNoteDetail>(serviceUrl, { noteId: noteId }).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getEsignNoteDetail', null))
    );
  }

  public getEsignLegalTextList(noteId: number): Observable<EsignLegalTextListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Contract/EsignLegalText';
    if (this._logging.serviceCallTraceLog) {
      console.log('ContractService.getEsignLegalTextList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<EsignLegalTextListItem>>(serviceUrl, { noteId: noteId }).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getEsignLegalTextList', []))
    );
  }

  public getEsignContract(serviceUrl: string): Observable<Blob> {
    if (this._logging.serviceCallTraceLog) {
      console.log('ContractService.getEsignContract :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.get(serviceUrl, { responseType: 'blob' });
  }

  public getNewPdfDocumentViewerDialogData(): PdfDocumentViewerDialogData {
    let pdfDocumentViewerDialogData: PdfDocumentViewerDialogData;
    pdfDocumentViewerDialogData = {
      dialogTitle: '',
      dialogSubTitle: null,
      errorTitle: '',
      acceptanceTitle: '',
      pdfUrl: null,
      requireScrollToEnd: false,
      viewOK: false
    };
    return pdfDocumentViewerDialogData;
  }

  public processNoteEsign(esignData: EsignData): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Contract/EsignData/Add';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContractService.processNoteEsign :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<number>(serviceUrl, esignData).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('processNoteEsign', null))
    );
  }
}
